import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Save, X, Pencil, Shield } from 'lucide-react';
import LoadingSpinner from '../LoadingSpinner';
import { usersService } from '../../services/usersService';
import { formatCurrency } from '../../utils/format';

const emptyDraft = () => ({
  email: '',
  name: '',
  contributed_capital: '',
  allowed: true,
  is_fund_admin: false,
});

function InvestorsTab({ onStatus, currentEmail }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(null);
  const [isNew, setIsNew] = useState(false);

  useEffect(() => {
    const unsub = usersService.subscribeToUsers(
      (data) => {
        setUsers(data);
        setLoading(false);
      },
      (err) => {
        onStatus(`Error loading investors: ${err.message}`, true);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [onStatus]);

  const startNew = () => {
    setDraft(emptyDraft());
    setIsNew(true);
  };

  const startEdit = (user) => {
    setDraft({
      email: user.email || user.id,
      name: user.name || '',
      contributed_capital: user.contributed_capital ?? '',
      allowed: user.allowed !== false,
      is_fund_admin: user.is_fund_admin === true,
    });
    setIsNew(false);
  };

  const save = async () => {
    try {
      onStatus('Saving investor...');
      await usersService.saveUser(draft);
      onStatus('Investor saved.');
      setDraft(null);
    } catch (err) {
      onStatus(`Error saving: ${err.message}`, true);
    }
  };

  const remove = async (user) => {
    const email = user.email || user.id;
    if (email === currentEmail) {
      onStatus('You cannot remove your own account.', true);
      return;
    }
    if (!window.confirm(`Remove access for ${email}?`)) return;
    try {
      await usersService.deleteUser(user.id);
      onStatus('Investor removed.');
    } catch (err) {
      onStatus(`Error removing: ${err.message}`, true);
    }
  };

  if (loading) return <LoadingSpinner text="Loading investors..." />;

  return (
    <div>
      <div className="a-toolbar">
        <h2 className="a-section-title">Accounts</h2>
        <div className="a-toolbar-actions">
          <button className="a-btn a-btn-primary" onClick={startNew}>
            <Plus size={16} /> Add investor
          </button>
        </div>
      </div>

      {draft && (
        <div className="a-card a-card-pad" style={{ marginBottom: 18 }}>
          <div className="a-form-grid">
            <div className="a-field">
              <label>Email{isNew ? '' : ' (locked)'}</label>
              <input
                className="a-input"
                type="email"
                value={draft.email}
                disabled={!isNew}
                onChange={(e) => setDraft({ ...draft, email: e.target.value })}
                placeholder="investor@example.com"
              />
            </div>
            <div className="a-field">
              <label>Name</label>
              <input
                className="a-input"
                type="text"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="Full name"
              />
            </div>
            <div className="a-field">
              <label>Contributed capital ($)</label>
              <input
                className="a-input"
                type="number"
                value={draft.contributed_capital}
                onChange={(e) => setDraft({ ...draft, contributed_capital: e.target.value })}
                placeholder="e.g., 100000"
              />
            </div>
          </div>
          <div className="a-toggle-row">
            <label className="a-toggle">
              <input
                type="checkbox"
                checked={draft.allowed}
                onChange={(e) => setDraft({ ...draft, allowed: e.target.checked })}
              />
              Access allowed
            </label>
            <label className="a-toggle">
              <input
                type="checkbox"
                checked={draft.is_fund_admin}
                onChange={(e) => setDraft({ ...draft, is_fund_admin: e.target.checked })}
              />
              Fund admin
            </label>
            <div className="a-toggle-actions">
              <button className="a-btn a-btn-ghost" onClick={() => setDraft(null)}>
                <X size={16} /> Cancel
              </button>
              <button className="a-btn a-btn-primary" onClick={save}>
                <Save size={16} /> Save
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="a-table-wrap">
        <table className="a-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th className="num">Contributed capital</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td>{user.name || '--'}</td>
                <td>{user.email || user.id}</td>
                <td className="num">{formatCurrency(user.contributed_capital)}</td>
                <td>
                  <span className={`a-pill ${user.allowed !== false ? 'ok' : 'off'}`}>
                    {user.allowed !== false ? 'Active' : 'Disabled'}
                  </span>
                  {user.is_fund_admin && (
                    <span className="a-pill admin">
                      <Shield size={11} /> Admin
                    </span>
                  )}
                </td>
                <td className="num">
                  <button className="a-icon-btn" onClick={() => startEdit(user)} title="Edit">
                    <Pencil size={15} />
                  </button>{' '}
                  <button
                    className="a-icon-btn danger"
                    onClick={() => remove(user)}
                    title="Remove"
                  >
                    <Trash2 size={15} />
                  </button>
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr>
                <td colSpan={5} className="a-empty-cell">
                  No investors yet. Add one above.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default InvestorsTab;
